import { createUseStyles } from 'react-jss';
import { canHover, canTouch, Statuses, Timings, Easing, Radius, FontWeights, BreakPointsMedia } from '@picsart/ds-foundation';
import { ButtonVariants, ButtonElementTypes } from '../types/index.js';

const defineBackgroundColor = (variant, defferColor) => ((variant === ButtonVariants.Outline || variant === ButtonVariants.Text) ? 'transparent' : defferColor);
const defineBorderColor = (variant, defferColor) => (variant === ButtonVariants.Outline) && defferColor;
const defineColor = (variant, textColor, baseColor) => ((variant === ButtonVariants.Filled) ? textColor : baseColor);
const useSkinStyles = createUseStyles({
    '@keyframes rotate': {
        to: {
            transform: 'rotate(360deg)',
        },
    },
    default: {
        backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.base),
        borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.base),
        color: ({ skin, variant, isLoading }) => (isLoading ? 'transparent' : defineColor(variant, skin.text, skin.base)),
        borderStyle: ({ variant }) => (variant === ButtonVariants.Outline) && 'solid',
    },
    interactive: {
        [canHover()]: {
            '&:hover': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseAction),
            },
        },
        [canTouch()]: {
            '&:active': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseAction),
            },
        },
    },
    active: {
        backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseActive),
        borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseActive),
        color: ({ skin, variant }) => defineColor(variant, skin.textActive, skin.baseActive),
    },
    activeInteraction: {
        [canHover()]: {
            '&:hover': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseActiveAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseActiveAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseActiveAction),
            },
        },
        [canTouch()]: {
            '&:active': {
                backgroundColor: ({ skin, variant }) => defineBackgroundColor(variant, skin.baseActiveAction),
                borderColor: ({ skin, variant }) => defineBorderColor(variant, skin.baseActiveAction),
                color: ({ skin, variant }) => defineColor(variant, skin.textAction, skin.baseActiveAction),
            },
        },
    },
    disabled: {
        opacity: Statuses.disabled,
        pointerEvents: 'none',
    },
    loadingIcon: {
        color: ({ skin, variant }) => defineColor(variant, skin.text, skin.base),
        animation: `$rotate ${Timings.Long} ${Easing.Linear} infinite`,
    },
}, {
    name: 'button',
});
const useLayoutStyles = createUseStyles({
    root: {
        display: 'inline-flex',
        verticalAlign: 'top',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: '100%',
        border: ({ Tag, variant }) => (Tag === ButtonElementTypes.Button && variant !== ButtonVariants.Outline) && 'none',
        width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.Default?.value?.height : null),
        minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.Default?.value?.height : 'initial'),
        height: ({ sizes }) => sizes.Default.value.height,
        padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.Default.value.paddingHorizontal}`,
        borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.Default.value.borderRadius),
        borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.Default.value.borderWidth,
        columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.Default.value.gap,
        fontWeight: FontWeights.SemiBold,
        fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.Default.value.fontSize),
        transitionProperty: 'background-color, border, color',
        transitionTimingFunction: Easing.Ease,
        transitionDuration: Timings.Default,
        cursor: ({ Tag, isDisabled, isLoading }) => (isLoading ? 'default' : isDisabled ? 'not-allowed' : Tag !== ButtonElementTypes.Link && 'pointer'),
        [BreakPointsMedia.XXL]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XXL?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XXL?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.XXL.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.XXL.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.XXL.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XXL.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XXL.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XXL.value.fontSize),
        },
        [BreakPointsMedia.XL]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XL?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XL?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.XL.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.XL.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.XL.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XL.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XL.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XL.value.fontSize),
        },
        [BreakPointsMedia.LG]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.LG?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.LG?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.LG.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.LG.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.LG.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.LG.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.LG.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.LG.value.fontSize),
        },
        [BreakPointsMedia.MD]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.MD?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.MD?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.MD.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.MD.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.MD.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.MD.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.MD.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.MD.value.fontSize),
        },
        [BreakPointsMedia.SM]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.SM?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.SM?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.SM.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.SM.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.SM.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.SM.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.SM.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.SM.value.fontSize),
        },
        [BreakPointsMedia.XS]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XS?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XS?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.XS.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.XS.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.XS.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XS.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XS.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XS.value.fontSize),
        },
        [BreakPointsMedia.XXS]: {
            width: ({ isFullWidth, sizes, centerIcon }) => (isFullWidth ? '100%' : centerIcon ? sizes.XS?.value?.height : null),
            minWidth: ({ sizes, centerIcon }) => (centerIcon ? sizes.XXS?.value?.height : 'initial'),
            height: ({ sizes }) => sizes.XXS.value.height,
            padding: ({ sizes, centerIcon }) => !centerIcon && `0 ${sizes.XXS.value.paddingHorizontal}`,
            borderRadius: ({ centerIcon, sizes, isFullWidth }) => (centerIcon && !isFullWidth ? Radius.circle : sizes.XXS.value.borderRadius),
            borderWidth: ({ sizes, variant }) => variant === ButtonVariants.Outline && sizes.XXS.value.borderWidth,
            columnGap: ({ sizes, centerIcon }) => !centerIcon && sizes.XXS.value.gap,
            fontSize: ({ centerIcon, sizes }) => (centerIcon ? 0 : sizes.XXS.value.fontSize),
        },
    },
    icon: {
        display: 'inline-block',
        flex: ({ sizes }) => `0 0 ${sizes.Default.value.iconSize}px`,
        width: ({ sizes }) => sizes.Default.value.iconSize,
        height: ({ sizes }) => sizes.Default.value.iconSize,
        [BreakPointsMedia.XXL]: {
            flex: ({ sizes }) => `0 0 ${sizes.XXL.value.iconSize}px`,
            width: ({ sizes }) => sizes.XXL.value.iconSize,
            height: ({ sizes }) => sizes.XXL.value.iconSize,
        },
        [BreakPointsMedia.XL]: {
            flex: ({ sizes }) => `0 0 ${sizes.XL.value.iconSize}px`,
            width: ({ sizes }) => sizes.XL.value.iconSize,
            height: ({ sizes }) => sizes.XL.value.iconSize,
        },
        [BreakPointsMedia.LG]: {
            flex: ({ sizes }) => `0 0 ${sizes.LG.value.iconSize}px`,
            width: ({ sizes }) => sizes.LG.value.iconSize,
            height: ({ sizes }) => sizes.LG.value.iconSize,
        },
        [BreakPointsMedia.MD]: {
            flex: ({ sizes }) => `0 0 ${sizes.MD.value.iconSize}px`,
            width: ({ sizes }) => sizes.MD.value.iconSize,
            height: ({ sizes }) => sizes.MD.value.iconSize,
        },
        [BreakPointsMedia.SM]: {
            flex: ({ sizes }) => `0 0 ${sizes.SM.value.iconSize}px`,
            width: ({ sizes }) => sizes.SM.value.iconSize,
            height: ({ sizes }) => sizes.SM.value.iconSize,
        },
        [BreakPointsMedia.XS]: {
            flex: ({ sizes }) => `0 0 ${sizes.XS.value.iconSize}px`,
            width: ({ sizes }) => sizes.XS.value.iconSize,
            height: ({ sizes }) => sizes.XS.value.iconSize,
        },
        [BreakPointsMedia.XXS]: {
            flex: ({ sizes }) => `0 0 ${sizes.XXS.value.iconSize}px`,
            width: ({ sizes }) => sizes.XXS.value.iconSize,
            height: ({ sizes }) => sizes.XXS.value.iconSize,
        },
        '& > path': {
            '&:not([fill])': {
                fill: 'currentColor',
            },
        },
    },
    content: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        maxWidth: '100%',
        textOverflow: 'ellipsis',
        display: 'block',
    },
    control: {
        display: 'none',
    },
    loading: {
        color: 'transparent',
        position: 'relative',
        pointerEvents: 'none',
    },
    loadingIcon: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        margin: 'auto',
        pointerEvents: 'none',
    },
}, {
    name: 'button-skin',
});

export { useLayoutStyles, useSkinStyles };
